/* eslint-disable default-case */
import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Card, Badge, message } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import FileDownload from 'js-file-download';
import moment from 'moment';
import Scrollbar from 'react-scrollbars-custom';
import 'moment/locale/pt-br';
import * as drawerSocietario from '~/containers/DrawerSocietario/actions';
import { request } from '~/components/Instance';
import { LoadingSkeleton, LoadingSkeletonContainer } from './styles';
import { PendingDashTasksDrawer } from './components/PendingDashTasksDrawer';
import ItemCard from './components/ItemCard';
import SocietarioHeader from './components/SocietarioHeader';
import {
  getTaskStages,
  getNextStageKey,
  getStagesFor,
  countStagesMarked,
  getDaysSinceLastActivity,
  getStageFilterOptions,
  getStageScope,
} from './helpers';
import { useSocietarioStages } from './stagesConfig';

import ArchivedTasksTable from './components/ArchivedTasksTable';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  SOCIETARIO_FILTERS_KEY,
  DONE_PERIODS,
  STALE_PERIODS,
  getDefaultSocietarioFilters,
  normalizeSocietarioFilters,
  countActiveSocietarioFilters,
  removeLegacySocietarioFilters,
  getSocietarioFiltersSummary,
} from './filters';

// Etapa da vez de um ticket. Sem nenhuma etapa marcada, é a primeira da lista
// do tipo; ticket finalizado não tem etapa da vez.
const getCurrentStageKey = (item) => {
  if (item.finalized_at) return null;
  const scope = getStageScope(item);
  const stages = getTaskStages(item);
  if (countStagesMarked(stages, scope) === 0) {
    const list = getStagesFor(scope);
    return list.length > 0 ? list[0].key : null;
  }
  return getNextStageKey(stages, scope);
};

const getListStyle = (isDraggingOver, listStyle) => ({
  background: listStyle.background,
  padding: 8,
  width: `100%`
});

const Dashboard = (props) => {
  const { list, getCustomer, getList, showDrawer, user, isLoading } = props;

  const [pendingDashTasksDrawer, setPendingDashTasksDrawer] = useState({
    isVisible: false,
  });

  // Filtros mantidos por alguns minutos após o refresh (por escritório/usuário/página).
  const persisted = usePersistedFilters(SOCIETARIO_FILTERS_KEY, getDefaultSocietarioFilters);
  const applied = normalizeSocietarioFilters(persisted.filters);
  const { screen, search, donePeriod, stageFilter, staleDays } = applied; // screen: active & archived
  const setFilters = persisted.setFilters;
  const filterCount = countActiveSocietarioFilters(applied, persisted.defaults);

  // A chave antiga ('societario_filtros') não tinha prazo nem escritório/usuário;
  // os filtros agora ficam em usePersistedFilters('societario.painel').
  useEffect(() => {
    removeLegacySocietarioFilters();
  }, []);

  // Recalcula as opções quando a lista do escritório chega da API.
  useSocietarioStages();
  const stageOptions = getStageFilterOptions();

  const setScreen = useCallback((value) => setFilters({ screen: value }), [setFilters]);
  const setSearch = useCallback((value) => setFilters({ search: value }), [setFilters]);
  const setStageFilter = useCallback((value) => setFilters({ stageFilter: value || null }), [setFilters]);
  const setStaleDays = useCallback((value) => setFilters({ staleDays: value }), [setFilters]);
  const setDonePeriod = useCallback((value) => setFilters({ donePeriod: value }), [setFilters]);

  const matchesSearch = useCallback((item) => {
    if (search === "") {
      return true;
    }

    const customer = getCustomer(item);
    const customer_name = typeof customer.name === 'string' ? customer.name.toLowerCase() : '';
    const customer_cod = typeof customer.cod === 'string' ? customer.cod.toLowerCase() : '';

    return customer_name.includes(search.toLowerCase()) || customer_cod.includes(search.toLowerCase());
  }, [search, getCustomer]);

  const matchesDonePeriod = useCallback((item) => {
    if (donePeriod === 0) {
      return true;
    }
    if (!item.finalized_at) {
      return true;
    }
    return moment(item.finalized_at).isAfter(moment().subtract(donePeriod, 'days'));
  }, [donePeriod]);

  const matchesStage = (item) => {
    if (!stageFilter) return true;
    return getCurrentStageKey(item) === stageFilter;
  };

  const matchesStale = (item) => {
    if (!staleDays) return true;
    if (item.finalized_at) return false;
    const days = getDaysSinceLastActivity(item);
    return days !== null && days >= staleDays;
  };

  const getListFiltered = (idList) => {
    return getList(idList).filter((item) => {
      if (!matchesSearch(item)) {
        return false;
      }
      if (!matchesStage(item) || !matchesStale(item)) {
        return false;
      }
      if (idList === 'done' && !matchesDonePeriod(item)) {
        return false;
      }
      return true;
    });
  };

  const lists = {
    not_started: { name: 'Não Iniciado', style: { background: '#f5f5f5', color: '#1890ff' } },
    in_process: { name: 'Em Processo', style: { background: '#f5f5f5', color: '#faad14' } },
    done: { name: 'Concluido', style: { background: '#f5f5f5', color: '#52c41a' } },
  };

  const openPendingDashTasksDrawer = useCallback(() => {
    setPendingDashTasksDrawer({
      isVisible: true,
    });
  }, []);

  const closePendingDashTasksDrawer = useCallback(() => {
    setPendingDashTasksDrawer({
      isVisible: false,
    });
  }, []);

  const onDragEnd = useCallback((result) => {
    const { updateList, list } = props;
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    // Reordenar dentro da propria coluna nao muda nada: a ordem exibida e sempre
    // por updated_at e a coluna `order` da tabela nunca foi gravada. Antes este
    // caminho reescrevia a lista do Redux com o subconjunto FILTRADO, e os cards
    // escondidos pela busca sumiam do estado ate recarregar a pagina.
    if (source.droppableId === destination.droppableId) {
      return;
    }

    const moved = getListFiltered(source.droppableId)[source.index];
    if (!moved) {
      return;
    }

    props.updateTask(moved.id, destination.droppableId);

    // Espelha localmente o que updateStatus() faz no servidor, para que o card
    // continue na coluna certa se as listas forem recalculadas antes do proximo
    // carregamento.
    const now = moment().format('YYYY-MM-DD HH:mm:ss');
    const stamped = { ...moved, updated_at: now };

    if (destination.droppableId === 'not_started') {
      stamped.processed_at = null;
      stamped.finalized_at = null;
    } else if (destination.droppableId === 'in_process') {
      stamped.processed_at = now;
      stamped.finalized_at = null;
    } else if (destination.droppableId === 'done') {
      stamped.finalized_at = now;
    }

    updateList(source.droppableId, list[source.droppableId].filter((item) => item.id !== moved.id));
    updateList(destination.droppableId, [stamped, ...list[destination.droppableId]]);
  }, [props, getListFiltered]);

  // Recebe a TAREFA (e assim que o ItemCard chama). Alem do parametro trocado,
  // a comparacao era `=== 1` contra um booleano vindo do JSON, entao o titulo
  // verde de "cliente com contrato" nunca chegou a aparecer.
  const hasContract = useCallback((item) => {
    const customer = item.customer;
    return !!(customer && (customer.has_contract || customer.has_rescission));
  }, []);

  const handleExportToExcel = useCallback(async () => {
    try {
      const response = await request('GET', `/dashtasks/export/excel`, null, true, {
        responseType: 'blob',
      });
      FileDownload(response.data, `Societário_${moment().format('DD-MM-YYYY_HH-mm-ss')}.xlsx`);
    } catch (error) {
      message.error('Não foi possível realizar a exportação!');
    }
  }, []);

  return (
    <div>
      <SocietarioHeader
        user={user}
        openPendingDashTasksDrawer={openPendingDashTasksDrawer}
        showDrawer={showDrawer}
        handleExportToExcel={handleExportToExcel}
        list={list}
        isSocietarioDrawerVisible={props.isSocietarioDrawerVisible}
        screen={screen}
        setScreen={setScreen}
        search={search}
        setSearch={setSearch}
        donePeriod={donePeriod}
        setDonePeriod={setDonePeriod}
        donePeriods={DONE_PERIODS}
        // Só o filtro de período: é ele que o aviso "fora do período" explica.
        doneVisibleCount={getList('done').filter((item) => matchesSearch(item) && matchesDonePeriod(item)).length}
        stageFilter={stageFilter}
        setStageFilter={setStageFilter}
        stageOptions={stageOptions}
        staleDays={staleDays}
        setStaleDays={setStaleDays}
        stalePeriods={STALE_PERIODS}
        defaultDonePeriod={persisted.defaults.donePeriod}
        filterCount={filterCount}
        hasFiltersToClear={!persisted.isDefault}
        onClearAllFilters={persisted.resetFilters}
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={getSocietarioFiltersSummary(applied, persisted.defaults, stageOptions)}
        onReset={persisted.resetFilters}
        onDismiss={persisted.dismissRestored}
      />

      {screen === 'archived' && (
        <ArchivedTasksTable 
          data={list.archived} 
          getCustomer={getCustomer} 
          showDrawer={showDrawer} 
        />
      )}

      {screen === 'active' && (
      <Row gutter={16}>
        <DragDropContext onDragEnd={onDragEnd}>
          {Object.keys(lists).map((idList) => (
            <Col span={8} key={idList}>
              <Droppable droppableId={idList}>
                {(provided, snapshot) => (
                  <Card
                    title={lists[idList].name}
                    bodyStyle={{ padding: 0 }}
                    headStyle={lists[idList].style}
                    extra={
                      <Badge
                        count={getListFiltered(idList).length}
                        style={{
                          backgroundColor: '#fff',
                          color: '#999',
                          boxShadow: '0 0 0 1px #d9d9d9 inset',
                        }}
                      />
                    }
                  >
                    <div
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver, lists[idList].style)}
                    >
                      <Scrollbar style={{ height: window.innerHeight - 240 }}>
                        {isLoading
                          ? Array(5)
                              .fill(1)
                              .map((_, index) => (
                                <LoadingSkeletonContainer key={index}>
                                  <LoadingSkeleton />
                                </LoadingSkeletonContainer>
                              ))
                          : getListFiltered(idList).map((item, index) => (
                              <Draggable key={item.id} draggableId={item.id} index={index}>
                                {(provided, snapshot) => (
                                  <ItemCard
                                    item={item}
                                    provided={provided}
                                    snapshot={snapshot}
                                    getCustomer={getCustomer}
                                    showDrawer={showDrawer}
                                    hasContract={hasContract}
                                  />
                                )}
                              </Draggable>
                            ))}
                        {provided.placeholder}
                      </Scrollbar>
                    </div>
                  </Card>
                )}
              </Droppable>
            </Col>
          ))}
        </DragDropContext>
      </Row>
      )}
      <PendingDashTasksDrawer
        isVisible={pendingDashTasksDrawer.isVisible}
        onClose={closePendingDashTasksDrawer}
        pendingDashTasks={props.list.pending}
        showDrawer={props.showDrawer}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state.dashTasks,
  user: state.user,
  isSocietarioDrawerVisible: state.drawerSocietario.drawer.show,
  isLoading: state.dashTasks.isLoading,
  getList: (list) =>
    state.dashTasks[list].sort(function (a, b) {
      return new Date(b.updated_at) - new Date(a.updated_at);
    }),
  getCustomer: (item) => {
    if (item.deleted_customer) {
      return item.deleted_customer;
    }

    try {
      const customer = state.customersById[item.customer_id];
      if (customer) {
        return customer;
      }
    } catch (error) {
      console.log(error);
    }

    // A propria tarefa carrega nome e codigo do cliente, inclusive quando ele foi
    // excluido ou ainda nao entrou na lista da store (uma Abertura recem-criada
    // nasce com o cliente desabilitado). Sem este fallback o card ficava '- - -'.
    if (item.customer && item.customer.name) {
      return item.customer;
    }

    return {
      name: '-    -    -',
    };
  },
});

const mapDispatchToProps = (dispatch) => ({
  updateList: (list, data) =>
    dispatch({
      type: 'UPDATE_DASHTASK_LIST',
      payload: {
        list,
        data,
      },
    }),
  showDrawer: (category, data) => dispatch(drawerSocietario.show(category, data)),
  updateTask: (id, list) =>
    dispatch({
      type: 'UPDATE_DASHTASKS_TASK',
      payload: {
        request: {
          method: 'put',
          url: `/dashtasks/${id}/status/${list}`,
        },
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
